import React from 'react';

import { Flex, MultiSelect, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { MultiSelectItem } from 'components/MultiSelect/MultiSelectItem';
import { MultiSelectValue } from 'components/MultiSelect/MultiSelectValue';
import { SelectItem } from 'components/SelectItem';
import { useTranslation } from 'react-i18next';
import { LanguageService } from 'services/language-service';
import { ConversationFilter } from 'shared/constants/conversations';
import { HandoffStatus, TakeoverStatus } from 'shared/interfaces/Conversation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import {
  HANDOFF_STATUS_SELECT_DATA,
  TAKEOVER_STATUS_SELECT_DATA,
  getSourceTypeSelectDataBySmartAssistantType
} from 'shared/utils/conversation';
import { DateRangePickerValue } from 'shared/utils/date';

interface FilterControlsProps {
  dateRange: DateRangePickerValue;
  setDateRange: (value: DateRangePickerValue) => void;
  handoffStatus?: HandoffStatus;
  setHandoffStatus?: (value: HandoffStatus) => void;
  takeoverStatus?: TakeoverStatus;
  setTakeoverStatus?: (value: TakeoverStatus) => void;
  sourceTypes?: string[];
  setSourceTypes?: (value: string[]) => void;
  setForcedConversationShowed?: (value: boolean) => void;
  visibleFilters: ConversationFilter[];
  smartAssistant: SmartAssistant;
}

export function FilterControls({
  dateRange,
  setDateRange,
  handoffStatus,
  setHandoffStatus,
  takeoverStatus,
  setTakeoverStatus,
  sourceTypes,
  setSourceTypes,
  setForcedConversationShowed,
  visibleFilters,
  smartAssistant
}: FilterControlsProps) {
  const { t } = useTranslation();

  return (
    <Flex direction="row" align="center" wrap="wrap" w="100%" gap="md">
      {visibleFilters.includes('dateRange') && (
        <DatePickerInput
          type="range"
          w={330}
          locale={LanguageService.getCurrentLanguage()}
          label={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.SELECT_DATE_RANGE')}
          value={dateRange}
          onChange={(value) => {
            setDateRange(value);
            setForcedConversationShowed?.(true);
          }}
        />
      )}
      {visibleFilters.includes('handoffStatus') && (
        <Select
          label={
            <InputLabelWithInfo
              mb={0}
              label={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.LABEL')}
              infoContentTranslationKey="SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.INFO"
            />
          }
          itemComponent={SelectItem}
          data={HANDOFF_STATUS_SELECT_DATA}
          onChange={(value) => {
            setHandoffStatus?.(value as HandoffStatus);
            setForcedConversationShowed?.(true);
          }}
          value={handoffStatus}
        />
      )}
      {visibleFilters.includes('takeoverStatus') && (
        <Select
          label={
            <InputLabelWithInfo
              mb={0}
              label={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.LABEL')}
              infoContentTranslationKey="SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.INFO"
            />
          }
          itemComponent={SelectItem}
          data={TAKEOVER_STATUS_SELECT_DATA}
          onChange={(value) => {
            setTakeoverStatus?.(value as TakeoverStatus);
            setForcedConversationShowed?.(true);
          }}
          value={takeoverStatus}
        />
      )}
      {visibleFilters.includes('sourceTypes') && (
        <MultiSelect
          miw={330}
          maxDropdownHeight={300}
          data={getSourceTypeSelectDataBySmartAssistantType(smartAssistant.type)}
          value={sourceTypes}
          onChange={(value) => {
            setSourceTypes?.(value);
            setForcedConversationShowed?.(true);
          }}
          valueComponent={MultiSelectValue}
          itemComponent={MultiSelectItem}
          label={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.SOURCES.LABEL')}
          placeholder={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.SOURCES.PLACEHOLDER')}
          style={{ flexGrow: 1 }}
        />
      )}
    </Flex>
  );
}
